/**
 * PasskeyLoginButton — V1.9.8
 *
 * A dedicated button for the login pages. Shows only when the current
 * browser supports WebAuthn AND advertises a platform authenticator
 * (Face ID / Touch ID / Windows Hello / Android fingerprint). On tap:
 *   1. Ask the backend for a discoverable-credential challenge.
 *   2. Let the browser present the biometric sheet.
 *   3. Verify + issue the existing JWT cookies.
 *   4. Refresh AuthContext and navigate to the appropriate landing page.
 *
 * The callsite passes `mode="recruiter" | "candidate"` for role-mismatch UX.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Fingerprint } from "@phosphor-icons/react";
import { hasPlatformAuthenticator, loginWithPasskey } from "@/lib/passkey";
import { useAuth } from "@/context/AuthContext";

export default function PasskeyLoginButton({ mode = "recruiter" }) {
    const { setUser } = useAuth();
    const nav = useNavigate();
    const [supported, setSupported] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        hasPlatformAuthenticator().then((ok) => {
            if (alive) setSupported(ok);
        });
        return () => {
            alive = false;
        };
    }, []);

    if (!supported) return null;

    const tap = async () => {
        setBusy(true);
        try {
            const user = await loginWithPasskey();
            // Role gating — passkeys are role-agnostic on the wire, so
            // enforce the login page's audience here.
            if (mode === "recruiter" && user.role === "candidate") {
                toast.error("Candidates should use the candidate sign in page.");
                return;
            }
            if (mode === "candidate" && user.role !== "candidate") {
                toast.error("This passkey belongs to a recruiter account.");
                return;
            }
            setUser(user);
            toast.success(`Welcome back, ${user.name || user.email}`);
            if (mode === "candidate") {
                nav("/candidate/jobs", { replace: true });
            } else {
                nav(user.role === "superadmin" ? "/superadmin" : "/dashboard", {
                    replace: true,
                });
            }
        } catch (e) {
            toast.error(e.message || "Passkey sign-in failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={tap}
            disabled={busy}
            data-testid={`passkey-login-btn-${mode}`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-ink/20 bg-white text-ink text-sm font-bold rounded-md hover:border-cobalt hover:text-cobalt active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
            <Fingerprint size={18} weight="bold" />
            {busy ? "Waiting for biometric…" : "Sign in with a passkey"}
        </button>
    );
}
